import { LatLng } from 'leaflet';
import { Trail } from '../../models/Trail';
import { IMapPoint } from '../../models/interfaces/IMapPoint';

const toFiniteNumber = (
	value: string | number | undefined | null,
): number | null => {
	if (value === undefined || value === null || value === '') {
		return null;
	}
	const num = typeof value === 'number' ? value : Number(value);
	return Number.isFinite(num) ? num : null;
};

const isInLatLngRange = (lat: number, lng: number): boolean => {
	if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
		return false;
	}
	// Treat null-island placeholders as invalid input data.
	if (lat === 0 && lng === 0) {
		return false;
	}
	return true;
};

export const toLatLng = (point?: IMapPoint | null): LatLng | null => {
	if (!point) {
		return null;
	}
	const lat = toFiniteNumber(point.lat);
	const lng = toFiniteNumber(point.lng);
	if (lat === null || lng === null) {
		return null;
	}
	if (!isInLatLngRange(lat, lng)) {
		return null;
	}
	return new LatLng(lat, lng);
};

export const mapPointsToLatLng = (points: IMapPoint[] = []): LatLng[] => {
	return points
		.map((point) => toLatLng(point))
		.filter((value): value is LatLng => value !== null);
};

const dedupeAdjacentLatLng = (points: LatLng[]): LatLng[] => {
	if (points.length <= 1) {
		return points;
	}
	const deduped: LatLng[] = [points[0]];
	for (let idx = 1; idx < points.length; idx++) {
		const prev = deduped[deduped.length - 1];
		const next = points[idx];
		if (prev.lat !== next.lat || prev.lng !== next.lng) {
			deduped.push(next);
		}
	}
	return deduped;
};

const firstValidPoiLatLng = (points: IMapPoint[] = []): LatLng | null => {
	const poi = points.find((point) => point.is_poi && toLatLng(point) !== null);
	return poi ? toLatLng(poi) : null;
};

const firstValidLatLng = (points: IMapPoint[] = []): LatLng | null => {
	for (const point of points) {
		const latLng = toLatLng(point);
		if (latLng) {
			return latLng;
		}
	}
	return null;
};

const mapPointsWithFallback = (
	points: IMapPoint[] = [],
	fallback: LatLng | null,
): LatLng[] => {
	return points
		.map((point) => toLatLng(point) ?? fallback)
		.filter((value): value is LatLng => value !== null);
};

const parseStepNumber = (title?: string): number | null => {
	if (!title) {
		return null;
	}
	const match = title.match(/step\s+(\d+)/i);
	if (!match || !match[1]) {
		return null;
	}
	const parsed = Number(match[1]);
	return Number.isFinite(parsed) ? parsed : null;
};

type IndexedStepPoint = {
	point: IMapPoint;
	index: number;
	step: number | null;
};

const distanceScore = (
	current: LatLng | null,
	previous: LatLng | null,
	next: LatLng | null,
): number => {
	if (!current) {
		return Number.POSITIVE_INFINITY;
	}
	if (previous && next) {
		return previous.distanceTo(current) + current.distanceTo(next);
	}
	if (previous) {
		return previous.distanceTo(current);
	}
	if (next) {
		return current.distanceTo(next);
	}
	return 0;
};

const findNextStepReference = (
	sorted: IndexedStepPoint[],
	startIndex: number,
): LatLng | null => {
	for (let idx = startIndex; idx < sorted.length; idx++) {
		const step = sorted[idx].step;
		if (step === null) {
			continue;
		}
		const currentStep = step;
		const candidates: LatLng[] = [];
		while (idx < sorted.length && sorted[idx].step === currentStep) {
			const latLng = toLatLng(sorted[idx].point);
			if (latLng) {
				candidates.push(latLng);
			}
			idx++;
		}
		if (candidates.length === 0) {
			continue;
		}
		const lat = candidates.reduce((sum, p) => sum + p.lat, 0) / candidates.length;
		const lng = candidates.reduce((sum, p) => sum + p.lng, 0) / candidates.length;
		return new LatLng(lat, lng);
	}
	return null;
};

const collapseDuplicateStepsByContinuity = (
	sorted: IndexedStepPoint[],
): IndexedStepPoint[] => {
	const collapsed: IndexedStepPoint[] = [];
	let previousKept: LatLng | null = null;

	for (let idx = 0; idx < sorted.length; ) {
		const current = sorted[idx];
		if (current.step === null) {
			collapsed.push(current);
			previousKept = toLatLng(current.point) ?? previousKept;
			idx++;
			continue;
		}

		const step = current.step;
		const group: IndexedStepPoint[] = [];
		while (idx < sorted.length && sorted[idx].step === step) {
			group.push(sorted[idx]);
			idx++;
		}

		if (group.length === 1) {
			collapsed.push(group[0]);
			previousKept = toLatLng(group[0].point) ?? previousKept;
			continue;
		}

		const nextReference = findNextStepReference(sorted, idx);
		let best = group[0];
		let bestScore = Number.POSITIVE_INFINITY;
		for (const candidate of group) {
			const candidateLatLng = toLatLng(candidate.point);
			const score = distanceScore(candidateLatLng, previousKept, nextReference);
			if (score < bestScore) {
				best = candidate;
				bestScore = score;
			}
		}

		collapsed.push(best);
		previousKept = toLatLng(best.point) ?? previousKept;
	}

	return collapsed;
};

const sortPointsByStepIfReliable = (points: IMapPoint[] = []): IMapPoint[] => {
	if (points.length < 3) {
		return points;
	}

	const indexed: IndexedStepPoint[] = points.map((point, index) => ({
		point,
		index,
		step: parseStepNumber(point.title),
	}));
	const withStep = indexed.filter((item) => item.step !== null);

	// Conservative guard: only reorder when most points have parseable step values.
	if (withStep.length / points.length < 0.8) {
		return points;
	}

	const uniqueSteps = new Set(withStep.map((item) => item.step)).size;
	if (uniqueSteps < 3) {
		return points;
	}

	const firstStepIndex = Math.min(...withStep.map((item) => item.index));
	const lastStepIndex = Math.max(...withStep.map((item) => item.index));

	// If unnumbered points appear in the middle of numbered steps, preserve
	// original order to avoid introducing synthetic jumps in the route.
	const hasNonStepInsideStepRange = indexed.some(
		(item) =>
			item.step === null &&
			item.index > firstStepIndex &&
			item.index < lastStepIndex,
	);
	if (hasNonStepInsideStepRange) {
		return points;
	}

	const stepOrdered = indexed
		.filter((item) => item.step !== null)
		.sort((a, b) => {
			if (a.step !== null && b.step !== null) {
				if (a.step !== b.step) {
					return a.step - b.step;
				}
				return a.index - b.index;
			}
			return a.index - b.index;
		});

	const leadingNonStep = indexed
		.filter((item) => item.step === null && item.index <= firstStepIndex)
		.sort((a, b) => a.index - b.index);
	const trailingNonStep = indexed
		.filter((item) => item.step === null && item.index >= lastStepIndex)
		.sort((a, b) => a.index - b.index);

	const hasDuplicateSteps =
		withStep.length > 0 && uniqueSteps < withStep.length;
	const normalized = hasDuplicateSteps
		? collapseDuplicateStepsByContinuity(stepOrdered)
		: stepOrdered;

	return [...leadingNonStep, ...normalized, ...trailingNonStep].map(
		(item) => item.point,
	);
};

export const getTrailPathLatLngs = (trail: Trail): LatLng[] => {
	const orderedPoints = sortPointsByStepIfReliable(trail.points);
	const fallback =
		firstValidPoiLatLng(trail.waypoints) ??
		firstValidPoiLatLng(orderedPoints) ??
		firstValidLatLng(trail.waypoints) ??
		firstValidLatLng(orderedPoints);

	const fromPoints = dedupeAdjacentLatLng(
		mapPointsWithFallback(orderedPoints, fallback),
	);
	if (fromPoints.length > 1) {
		return fromPoints;
	}

	const fromWaypoints = dedupeAdjacentLatLng(mapPointsToLatLng(trail.waypoints));
	if (fromWaypoints.length > 0) {
		return fromWaypoints;
	}

	return fromPoints;
};
